import { useApolloClient, useMutation } from '@apollo/client';
import { useMemo, useReducer, useRef } from 'react';
import { featureCollection } from '@turf/turf';
import _ from 'lodash';

import { customLayerSpec, userLayers } from '../constants/map';
import { CUSTOM_LAYER_TYPE } from '../constants/layers';
import {
  BulkCreateLayerFeatures,
  CreateLayerFeature,
  DeleteLayer,
  DeleteLayerFeature,
  UpsertLayer,
} from '../mutations';
import {
  generateGeojsonFeatureId,
  getLayerIdParams,
  layerFeatureIdFromGeojsonFeatureId,
  layerIdFromGeojsonFeatureId,
  layerIsCustom,
  sortLayers,
} from '../utils/layers';
import { gzipIfNeeded } from '../utils/gzip';
import { generateDbId, MODEL_PREFIXES, removeKeys } from '../utils/utils';
import { LayerSignedPutUrlQuery } from '../queries';
import useThrottledFn from './useThrottledFn';
import { mutateAndDisplayErrors, showError } from '../utils/toast';

const DEFAULT_THROTTLE_TIME_MS = 500;

/**
 * Creates a new throttled function for each layerId + farmId combo.
 *
 * NOTE: `fn` must accept a single object argument that includes `layerId`.
 *
 * This is necessary since with a single throttled function this can happen:
 *
 * 1. Call `fn({ layerId: 1, data })` -> `fn` is called
 * 2. Call `fn({ layerId: 2, data })` -> `fn` is throttled, never called (!)
 * 3. Call `fn({ layerId: 3, data })` -> `fn` is throttled, never called (!)
 * 4. Call `fn({ layerId: 4, data })` -> `fn` is throttled, called after delay
 *
 * In this case, layers 2 and 3 never get their data.
 *
 * This solution ensures that the throttling is per-layer and thus avoids this issue.
 */
const useThrottledFnByLayer = (fn, delay = DEFAULT_THROTTLE_TIME_MS) =>
  useThrottledFn(fn, ['layerId'], delay);

const initialLayerState = Object.entries(userLayers).reduce(
  (obj, [layerId, properties]) =>
    Object.assign(obj, {
      [layerId]: {
        layerId,
        layerType: layerId, // For non-custom layers, layerId and layerType are the same
        featuresShown: {},
        shown: true,
        /**
         * NOTE: this `layerFeatureRecords` key corresponds to the LayerFeature model,
         * and is currently only used for custom Layers. The actual data of the
         * layer features (the geojson) is stored in the `data` key (below).
         *
         * This is keyed by `LayerFeature.id`.
         */
        layerFeatureRecords: {},
        data: null,
        ...properties,
      },
    }),
  {},
);

const layersReducer = (layers, action) => {
  switch (action.type) {
    case 'toggle': {
      const layer = layers[action.layerId];
      return {
        ...layers,
        [action.layerId]: {
          ...layer,
          shown: !layer.shown,
        },
      };
    }
    case 'toggleFeature': {
      const layerId = layerIdFromGeojsonFeatureId(action.featureId);
      const layer = layers[layerId];
      return {
        ...layers,
        [layerId]: {
          ...layer,
          featuresShown: {
            ...layer.featuresShown,
            [action.featureId]: !layer.featuresShown[action.featureId],
          },
        },
      };
    }
    case 'setShown': {
      const layer = layers[action.layerId];
      return {
        ...layers,
        [action.layerId]: {
          ...layer,
          shown: true,
        },
      };
    }
    case 'bulkSetShown': {
      // Set all layers to shown or hidden
      return Object.fromEntries(
        Object.entries(layers).map(([layerId, layer]) => [
          layerId,
          {
            ...layer,
            shown: action.shown,
          },
        ]),
      );
    }
    case 'setLayer': {
      const layer = layers[action.layerId];
      return {
        ...layers,
        [action.layerId]: {
          layerId: action.layerId,
          ...layer,
          ...action.layerState,
        },
      };
    }
    case 'deleteLayer': {
      return Object.fromEntries(
        Object.entries(layers).filter(([key]) => key !== action.layerId),
      );
    }
    case 'reset': {
      return initialLayerState;
    }
    default:
      throw new Error(`Invalid type: ${action.type}.`);
  }
};

const useLayers = () => {
  const apolloClient = useApolloClient();

  // MUTATIONS
  const [upsertLayerMutation] = useMutation(UpsertLayer);
  const [deleteLayerMutation] = useMutation(DeleteLayer);
  const [createLayerFeatureMutation] = useMutation(CreateLayerFeature);
  const [deleteLayerFeatureMutation] = useMutation(DeleteLayerFeature);
  const [bulkCreateLayerFeaturesMutation] = useMutation(
    BulkCreateLayerFeatures,
  );

  // STATE
  const [layers, layerDispatch] = useReducer(layersReducer, initialLayerState);

  // Using ref so we can access this state in callbacks without it being stale
  const layersRef = useRef(layers);
  layersRef.current = layers;

  // UPDATES
  const setLayer = (layerId, layerState) => {
    layerDispatch({ type: 'setLayer', layerId, layerState });
  };

  const toggleLayer = (layerId) => {
    layerDispatch({ type: 'toggle', layerId });
  };

  const toggleLayerFeature = (featureId) => {
    layerDispatch({ type: 'toggleFeature', featureId });
  };

  const deleteLayer = (layerId) => {
    layerDispatch({ type: 'deleteLayer', layerId });
  };

  // We throttle this so that arrow keys in the width input do not cause a high frequency of requests.
  const setLayerWidthRemote = useThrottledFnByLayer(
    async ({ farmId, layerId, width }) => {
      const input = { farmId, width, ...getLayerIdParams(layerId) };

      const result = await mutateAndDisplayErrors(() =>
        upsertLayerMutation({ variables: { input } }),
      );

      // Store the new updatedAt value locally.
      setLayer(layerId, { updatedAt: result.updatedAt });
    },
  );

  const setLayerWidth = (layerId, farmId, width, { upload = true } = {}) => {
    setLayer(layerId, { width });

    if (upload) {
      setLayerWidthRemote({ layerId, farmId, width });
    }
  };

  const getUploadUrl = async ({ layerId, farmId }) => {
    const variables = layerId.startsWith(`${MODEL_PREFIXES.LAYER}_`)
      ? { id: layerId, layerType: CUSTOM_LAYER_TYPE, farmId }
      : { layerType: layerId, farmId };

    const response = await apolloClient.query({
      query: LayerSignedPutUrlQuery,
      variables,
      // Right now, the signedPutUrl expires after 15 minutes
      // So, fetch a new one each time
      fetchPolicy: 'no-cache',
    });

    const result = response.data?.layerSignedPutUrl;
    if (!result) throw new Error('Failed to get upload URL for Layer.');

    return result;
  };

  const updateS3VersionId = async ({ farmId, layerId, s3VersionId }) => {
    // Upsert Layer w/ new s3VersionId value.
    const input = { farmId, s3VersionId, ...getLayerIdParams(layerId) };
    const upsertResponse = await upsertLayerMutation({
      variables: { input },
    });

    // Update local state with new updatedAt value
    const { result, errors } = upsertResponse?.data?.upsertLayer ?? {};
    if (errors || !result) {
      throw new Error(errors || ['Failed to upsert layer.']);
    }
    const { updatedAt } = result;
    setLayer(layerId, { updatedAt });
  };

  /**
   * Uploads the geo data to S3 and returns the new S3 version ID.
   *
   * NOTE: since this function returns a non-void Promise, we can't throttle it
   * directly, so functions that call this function should be throttled to avoid
   * uploads that are too frequent.
   */
  const uploadGeodata = async ({ layerId, farmId, data }) => {
    try {
      const url = await getUploadUrl({ layerId, farmId });
      const json = JSON.stringify(data);
      const { body, headers } = await gzipIfNeeded(json);

      const s3Response = await fetch(url, {
        method: 'PUT',
        body,
        headers: {
          'content-type': 'application/json',
          // Set Type=Layer tag to apply lifecycle rules to layer objects
          'x-amz-tagging': 'Type=Layer',
          ...headers,
        },
      });
      if (!s3Response.ok) {
        throw new Error(`Cannot save Layer: ${s3Response.statusText}`);
      }

      return s3Response.headers.get('x-amz-version-id');
    } catch (error) {
      showError(error);
      return null;
    }
  };

  // Here, we create a throttled function per layerId. This way, we don't miss any layer data uploads due to throttling.
  const throttledUploadGeodata = useThrottledFnByLayer(
    async ({ farmId, layerId, data }) => {
      const s3VersionId = await uploadGeodata({ farmId, layerId, data });
      await updateS3VersionId({ farmId, layerId, s3VersionId });
    },
  );

  const setLayerData = async ({ layerId, farmId, data, upload = true }) => {
    setLayer(layerId, { data });
    if (upload) {
      try {
        await throttledUploadGeodata({ layerId, farmId, data });
      } catch (error) {
        showError(error);
      }
    }
  };

  const createLayerFeatureRemote = (variables) =>
    mutateAndDisplayErrors(
      () => createLayerFeatureMutation({ variables }),
      'Failed to create layer feature.',
    );

  const bulkCreateLayerFeaturesRemote = (variables) =>
    mutateAndDisplayErrors(
      () => bulkCreateLayerFeaturesMutation({ variables }),
      'Failed to create layer feature.',
    );

  const deleteLayerFeatureRemote = (input) =>
    mutateAndDisplayErrors(
      () => deleteLayerFeatureMutation({ variables: { input } }),
      'Failed to delete layer feature.',
    );

  /**
   * Returns `[feature, promise]` where `feature` is the new feature, and
   * `promise` resolves when the feature is created on the backend.
   */
  const addLayerFeature = (layerId, farmId, feature) => {
    // Since this function is called by mapbox-draw event handlers, we use a ref to avoid stale state.
    const layer = layersRef.current[layerId];

    const geojsonFeatureId = generateGeojsonFeatureId(layerId);

    const featureWithId = {
      ...feature,
      id: geojsonFeatureId,
      properties: {
        ...feature.properties,
        id: geojsonFeatureId,
      },
    };
    const layerFeatureRecordId = layerFeatureIdFromGeojsonFeatureId(
      geojsonFeatureId,
    );

    const newLayerData = featureCollection([
      ...(layer.data?.features || []),
      featureWithId,
    ]);

    const layerState = {
      data: newLayerData,
      featuresShown: { ...layer.featuresShown, [geojsonFeatureId]: true },
    };

    const isCustom = layerIsCustom(layer);
    if (isCustom) {
      // add new entry to layerFeatureRecords object
      layerState.layerFeatureRecords = {
        ...layer.layerFeatureRecords,
        [layerFeatureRecordId]: {
          id: layerFeatureRecordId,
        },
      };
    }

    // Update local state before waiting for remote update (optimistic rendering)
    setLayer(layerId, layerState);

    const promise = (async () => {
      try {
        // Upload the geojson, but for custom layers, don't update the s3VersionId yet,
        // since we'll do that as a part of the CreateLayerFeature mutation, in a
        // transaction.
        const layerS3VersionId = await uploadGeodata({
          layerId,
          farmId,
          data: newLayerData,
        });

        // For custom layers, create a LayerFeature on the backend
        // (this also updates the Layer s3 version id)
        if (isCustom) {
          const {
            updatedAt: layerUpdatedAt,
            layerFeature: { updatedAt: layerFeatureUpdatedAt },
          } = await createLayerFeatureRemote({
            id: layerFeatureRecordId,
            layerId,
            farmId,
            layerS3VersionId,
          });

          // NOTE: we don't use `layer` here because it might be stale.
          const { layerFeatureRecords = {} } = layersRef.current[layerId];

          // Update local state w/ new updatedAt value for Layer & LayerFeature
          setLayer(layerId, {
            layerFeatureRecords: {
              ...layerFeatureRecords,
              [layerFeatureRecordId]: {
                ...layerFeatureRecords[layerFeatureRecordId],
                updatedAt: layerFeatureUpdatedAt,
              },
            },
            updatedAt: layerUpdatedAt,
          });
        } else {
          // For non-custom layers, we still need to update the layer s3 version id
          await updateS3VersionId({
            farmId,
            layerId,
            s3VersionId: layerS3VersionId,
          });
        }
        return true; // success
      } catch (err) {
        showError('Failed to add layer feature');
        return false; // failure

        // NOTE: we don't revert local state here. This could cause issues, but
        // reverting local state could also cause issues if anthing's changed in
        // the meantime.
      }
    })();

    return [featureWithId, promise];
  };

  // Updates local state, uploads to s3, creates LayerFeatures on backend, and updates Layer.s3VersionId
  const bulkCreateLayerFeatures = async ({
    farmId,
    layerId,
    fullLayerData,
    newLayerFeatures,
    removeExisting = false,
  }) => {
    const layer = layersRef.current[layerId];
    const initialLayerData = { ...layer.data };
    const isCustom = layerIsCustom(layer);

    const newLayerFeatureRecordIds = newLayerFeatures.map((feature) =>
      layerFeatureIdFromGeojsonFeatureId(feature.properties.id),
    );

    const layerState = {
      data: fullLayerData,
      featuresShown: newLayerFeatures.reduce(
        (featuresShown, feature) => ({
          ...featuresShown,
          [feature.properties.id]: true,
        }),
        layer.featuresShown,
      ),
    };

    // mapping of `[id]: { id }` for layer state
    const newLayerFeatureRecords = newLayerFeatureRecordIds.reduce(
      (acc, id) => ({ ...acc, [id]: { id } }),
      {},
    );

    if (isCustom) {
      layerState.layerFeatureRecords = {
        ...layer.layerFeatureRecords,
        ...newLayerFeatureRecords,
      };
    }

    // Update local state before waiting for remote update (optimistic rendering)
    setLayer(layerId, layerState);

    try {
      // Upload the geojson, but for custom layers, don't update the s3VersionId yet,
      // since we'll do that as a part of the CreateLayerFeature mutation, in a
      // transaction.
      const layerS3VersionId = await uploadGeodata({
        layerId,
        farmId,
        data: fullLayerData,
      });

      // For custom layers, create LayerFeatures on the backend
      // (this also updates the Layer s3 version id)
      if (isCustom) {
        const {
          updatedAt: layerUpdatedAt,
          layerFeatures: updatedLayerFeatures,
        } = await bulkCreateLayerFeaturesRemote({
          input: {
            farmId,
            layerId,
            ids: newLayerFeatureRecordIds,
            layerS3VersionId,
            removeExisting,
          },
        });

        // Update local state w/ new updatedAt value for LayerFeature
        setLayer(layerId, {
          layerFeatureRecords: _.keyBy(updatedLayerFeatures, 'id'),
          updatedAt: layerUpdatedAt,
        });
      } else {
        // For non-custom layers, we still need to update the layer s3 version id
        await updateS3VersionId({
          farmId,
          layerId,
          s3VersionId: layerS3VersionId,
        });
      }
    } catch (err) {
      showError(err);

      // revert layer data
      setLayer(layerId, { data: initialLayerData });
    }
  };

  /**
   * NOTE: this is just used to change the geodata (width / name / isInnerFeature), and it does not impact the LayerFeature model.
   */
  const setLayerFeatureProperty = (
    featureId,
    farmId,
    property,
    value,
    { upload = true } = {},
  ) => {
    const layerId = layerIdFromGeojsonFeatureId(featureId);
    const { data } = layersRef.current[layerId];

    const newData = {
      ...data,
      features: data.features.map((feature) =>
        feature.properties.id === featureId
          ? {
              ...feature,
              properties: {
                ...feature.properties,
                [property]: value,
              },
            }
          : feature,
      ),
    };

    setLayerData({ layerId, farmId, data: newData, upload });
  };

  const setLayerFeatureCoordinates = (
    featureId,
    farmId,
    coordinates,
    { upload = true } = {},
  ) => {
    const layerId = layerIdFromGeojsonFeatureId(featureId);
    const { data } = layersRef.current[layerId];
    const newData = {
      ...data,
      features: data.features.map((feature) =>
        feature.properties.id === featureId
          ? {
              ...feature,
              geometry: {
                ...feature.geometry,
                coordinates,
              },
            }
          : feature,
      ),
    };

    setLayerData({ layerId, farmId, data: newData, upload });
  };

  // N.B. Assumes all features belong to same layer
  const deleteLayerFeatures = async (featureIds, farmId) => {
    if (!featureIds.length) return;

    const layerId = layerIdFromGeojsonFeatureId(featureIds[0]);
    const layerFeatureRecordIds = featureIds.map((featureId) =>
      layerFeatureIdFromGeojsonFeatureId(featureId),
    );
    const layer = layersRef.current[layerId];
    const { data, featuresShown, layerFeatureRecords } = layer;
    const isCustom = layerIsCustom(layer);

    // `updatedAt` is needed for the `deleteLayerFeatureRemote` mutation
    const layerFeatureTimestamps = Object.fromEntries(
      layerFeatureRecordIds.map((layerFeatureRecordId) => [
        layerFeatureRecordId,
        layerFeatureRecords[layerFeatureRecordId]?.updatedAt,
      ]),
    );

    const newData = {
      ...data,
      features: data.features.filter(
        (feature) => !featureIds.includes(feature.properties.id),
      ),
    };

    // Update local state before waiting for remote update (optimistic rendering)
    setLayer(layerId, {
      data: newData,
      featuresShown: removeKeys(featuresShown, featureIds),
      layerFeatureRecords: removeKeys(
        layerFeatureRecords,
        layerFeatureRecordIds,
      ),
    });

    // Upload the geojson
    const layerS3VersionId = await uploadGeodata(
      { layerId, farmId, data: newData },
      { shouldUpdateS3VersionId: !isCustom },
    );

    try {
      if (isCustom) {
        const layerTimestamps = layerFeatureRecordIds.map(
          async (layerFeatureRecordId) => {
            // Delete the LayerFeature and update the Layer's S3 version id (in 1 transaction)
            const { updatedAt } = await deleteLayerFeatureRemote({
              id: layerFeatureRecordId,
              updatedAt: layerFeatureTimestamps[layerFeatureRecordId],
              layerS3VersionId,
            });
            return updatedAt;
          },
        );
        const layerUpdatedAt = Math.max(layerTimestamps);

        // Update local state with Layer updatedAt
        setLayer(layerId, { updatedAt: layerUpdatedAt });
      } else {
        // for non-custom layers, we still need to update the layer s3 version id
        await updateS3VersionId({
          farmId,
          layerId,
          s3VersionId: layerS3VersionId,
        });
      }
    } catch (error) {
      showError(error);

      // NOTE: we don't revert local state here.
    }
  };

  const deleteLayerFeature = async (featureId, farmId) =>
    deleteLayerFeatures([featureId], farmId);

  /**
   * Returns `[id, promise]` where `id` is the new layer's id, and `promise` is
   * a promise that resolves when the layer is created on the backend.
   */
  const createCustomLayer = ({ name: optionalName, farmId }) => {
    // Generate an ID for the new layer locally so we can optimistically render it without needing to wait for the backend
    const id = generateDbId('LAYER');

    // Generate a unique name for the new layer, if needed
    const existingCustomLayers = Object.values(layers).filter(layerIsCustom)
      .length;
    const fallbackName = `Custom Layer ${existingCustomLayers + 1}`;
    const displayName = optionalName ?? fallbackName;

    // Data is initially empty
    const data = featureCollection([]);

    // Add the new layer to the layers state with empty data
    setLayer(id, {
      ...customLayerSpec,
      featuresShown: {},
      layerFeatureRecords: {},
      shown: true,
      displayName,
      data,
    });

    // Kick off the backend work without awaiting it so we can return the new layer id immediately
    const promise = (async () => {
      try {
        // Upload the initial (empty) data and then create the Layer with its s3VersionId
        const s3VersionId = await uploadGeodata({ layerId: id, farmId, data });
        const result = await mutateAndDisplayErrors(() =>
          upsertLayerMutation({
            variables: {
              input: {
                id,
                displayName,
                farmId,
                layerType: CUSTOM_LAYER_TYPE,
                s3VersionId,
              },
            },
          }),
        );

        // Update our local state with updatedAt
        const { updatedAt } = result;
        setLayer(id, { updatedAt });
      } catch (err) {
        // clean up
        deleteLayer(id);
        throw err;
      }
    })();

    return [id, promise];
  };

  const renameCustomLayer = async ({ layer, farmId, displayName }) => {
    const { layerId, displayName: existingName } = layer;

    // Update local state first, for optimistic rendering
    setLayer(layerId, { displayName });

    // Make backend update
    try {
      const result = await mutateAndDisplayErrors(
        () =>
          upsertLayerMutation({
            variables: {
              input: {
                id: layer.layerId,
                layerType: CUSTOM_LAYER_TYPE,
                displayName,
                farmId,
              },
            },
          }),
        `Failed to rename Custom Layer: ${existingName}.`,
      );

      // Update local state with updatedAt
      const { updatedAt } = result;
      setLayer(layerId, { updatedAt });
    } catch (err) {
      // revert local state
      setLayer(layerId, { displayName: existingName });
    }
  };

  const setCustomLayerColor = async ({ layer, farmId, color }) => {
    const { layerId, color: existingColor } = layer;

    const cleanup = () => {
      setLayer(layerId, { color: existingColor });
    };

    try {
      // Update local state first, for optimistic rendering
      setLayer(layerId, { color });

      // Make backend update
      const response = await upsertLayerMutation({
        variables: {
          input: {
            farmId,
            id: layer.layerId,
            layerType: CUSTOM_LAYER_TYPE,
            color,
          },
        },
      });
      const { result, errors } = response?.data?.upsertLayer ?? {};

      // Handle backend errors
      if (errors || !result) {
        showError(errors || 'Failed to change custom layer color');

        // revert local state
        cleanup();
        return;
      }

      // Update local state with updatedAt
      const { updatedAt } = result;
      setLayer(layerId, { updatedAt });
    } catch (error) {
      cleanup();
      showError(error);
    }
  };

  const deleteCustomLayer = async ({ layerId, updatedAt }) => {
    // Update the local state immediately for optimistic rendering
    layerDispatch({ type: 'deleteLayer', layerId });

    await mutateAndDisplayErrors(
      () =>
        deleteLayerMutation({
          variables: { input: { id: layerId, updatedAt } },
        }),
      'Failed to delete Custom Layer.',
    );
  };

  const resetLayers = () => {
    layerDispatch({ type: 'reset' });
  };

  const setLayerShown = ({ layerId }) => {
    layerDispatch({ type: 'setShown', layerId });
  };

  const bulkSetLayersShown = ({ shown }) => {
    layerDispatch({ type: 'bulkSetShown', shown });
  };

  // All features from all layers, keyed by feature id
  const allGeojsonFeatures = useMemo(() => {
    const arr = Object.values(layers)
      .map((layer) =>
        (layer.data?.features ?? []).map((feature, i) =>
          feature.properties.name
            ? feature
            : // Add fallback names to features that don't have names:
              {
                ...feature,
                properties: {
                  ...feature.properties,
                  name: `Feature ${i + 1}`,
                },
              },
        ),
      )
      .flat();
    return _.keyBy(arr, 'properties.id');
  }, [layers]);

  // All LayerFeature records, keyed by id
  const allLayerFeatureRecords = useMemo(
    () =>
      Object.values(layers)
        .filter(layerIsCustom)
        .reduce((acc, layer) => {
          const { layerFeatureRecords, layerId } = layer;
          Object.values(layerFeatureRecords).forEach((lf) => {
            acc[lf.id] = { ...lf, layerId };
          });
          return acc;
        }, {}),
    [layers],
  );

  const sortedLayers = useMemo(() => sortLayers(Object.values(layers)), [
    layers,
  ]);

  return {
    layers,
    sortedLayers,
    allGeojsonFeatures,
    allLayerFeatureRecords,
    toggleLayer,
    toggleLayerFeature,
    setLayerShown,
    bulkSetLayersShown,
    setLayer,
    setLayerWidth,
    setLayerData,
    setLayerFeatureProperty,
    setLayerFeatureCoordinates,
    deleteLayerFeature,
    deleteLayerFeatures,
    createCustomLayer,
    renameCustomLayer,
    deleteCustomLayer,
    setCustomLayerColor,
    resetLayers,
    addLayerFeature,
    bulkCreateLayerFeatures,
  };
};

export default useLayers;
